import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import NotificationService from '@/services/NotificationService';
import SoundManager from '@/services/SoundManager';
import { supabase } from '@/integrations/supabase/client';
import { useProgramContext } from '@/context/ProgramContext';

interface NotificationPreferences {
  sound_enabled: boolean;
  desktop_enabled: boolean;
  email_enabled: boolean;
  sound_volume: number;
}


export function useNotifications() {
  const queryClient = useQueryClient();
  const { currentProgram } = useProgramContext();
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof window !== 'undefined' && 'Notification' in window 
      ? Notification.permission 
      : 'default'
  );

  // Load notification preferences
  const { data: preferences, isLoading: preferencesLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => NotificationService.loadPreferences(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const result = await NotificationService.updatePreferences(updates);
      return result;
    },
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notification-preferences'] });
      
      // Snapshot the previous value
      const previousPreferences = queryClient.getQueryData(['notification-preferences']);
      
      // Optimistically update to the new value
      if (previousPreferences) {
        queryClient.setQueryData(['notification-preferences'], {
          ...previousPreferences,
          ...updates
        });
      }
      
      // Return a context object with the snapshotted value
      return { previousPreferences };
    },
    onSuccess: (data, variables) => {
      // Set the query data directly - no need to invalidate since we have the fresh data
      queryClient.setQueryData(['notification-preferences'], data);
      
      // Update sound manager settings
      if (variables.sound_enabled !== undefined) {
        SoundManager.setEnabled(variables.sound_enabled);
      }
      if (variables.sound_volume !== undefined) {
        SoundManager.setVolume(variables.sound_volume);
      }
    },
    onError: (error, variables, context) => {
      console.error('Failed to update notification preferences:', error);
      
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousPreferences) {
        queryClient.setQueryData(['notification-preferences'], context.previousPreferences);
      }
    }
  });


  // Mark message as read
  const markMessageAsRead = useMutation({
    mutationFn: (messageId: string) => 
      NotificationService.markMessageAsRead(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
    }
  });

  // Mark conversation as read
  const markConversationAsRead = useMutation({
    mutationFn: (conversationId: string) => 
      NotificationService.markConversationAsRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    }
  });

  // Request notification permission
  const requestPermission = useCallback(async () => {
    const result = await NotificationService.requestPermission();
    setPermission(result);
    return result;
  }, []);

  // Show notification
  const showNotification = useCallback(async (
    title: string, 
    options?: NotificationOptions
  ) => {
    await NotificationService.showNotification(title, options);
    
    // Play sound if enabled
    if (preferences?.sound_enabled) {
      await SoundManager.playNotification();
    }
  }, [preferences]);

  // Initialize sound manager with preferences
  useEffect(() => {
    if (preferences) {
      SoundManager.setEnabled(preferences.sound_enabled);
      SoundManager.setVolume(preferences.sound_volume);
    }
  }, [preferences]);

  // Setup real-time subscription for new messages
  useEffect(() => {
    // Only subscribe if we have a current program
    if (!currentProgram?.id) return;

    const channel = supabase
      .channel(`chat-notifications:${currentProgram.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `sender_type=eq.visitor,program_id=eq.${currentProgram.id}`
        },
        async (payload) => {
          // Show notification for new message
          const message = payload.new as any;
          
          if (message) {
            // Always play sound if enabled, regardless of desktop notification
            if (preferences?.sound_enabled) {
              await SoundManager.playNotification();
            }
            
            // Show desktop notification if enabled
            if (NotificationService.isNotificationEnabled()) {
              await showNotification(
                'New message',
                {
                  body: message.content?.substring(0, 100) || 'You have a new message',
                  tag: message.conversation_id,
                  data: {
                    conversationId: message.conversation_id,
                    messageId: message.id
                  }
                }
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [showNotification, preferences, currentProgram?.id]);


  // Initialize audio context on first user interaction
  useEffect(() => {
    const handleUserInteraction = () => {
      SoundManager.initialize();
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Wrapper function for updatePreferences that handles the mutation
  const updatePreferences = (updates: Partial<NotificationPreferences>) => {
    // Use mutate instead of mutateAsync for fire-and-forget behavior
    updatePreferencesMutation.mutate(updates);
  };

  return {
    preferences,
    preferencesLoading,
    updatePreferences,
    isUpdatingPreferences: updatePreferencesMutation.isPending,
    markMessageAsRead: markMessageAsRead.mutate,
    markConversationAsRead: markConversationAsRead.mutate,
    requestPermission,
    showNotification,
    permission,
    isNotificationEnabled: NotificationService.isNotificationEnabled(),
    playNotification: () => SoundManager.playNotification()
  };
}